"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import { useRole } from "@/lib/useRole";
import { TOURS, type TourStep } from "@/lib/onboarding";

const STORAGE_PREFIX = "onboarding_completed_";

export function OnboardingTour() {
  const { data: session } = useSession();
  const { role, loading } = useRole();
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [highlightStyle, setHighlightStyle] = useState<React.CSSProperties>({});
  const [transitioning, setTransitioning] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Filter steps to only those whose target element exists in the DOM
  const allSteps = TOURS[role] ?? [];
  const [validSteps, setValidSteps] = useState<TourStep[]>([]);

  const storageKey = `${STORAGE_PREFIX}${role}`;

  // Check if tour should auto-start
  useEffect(() => {
    if (loading || !session?.user || allSteps.length === 0) return;
    const completed = localStorage.getItem(storageKey);
    if (completed) return;

    const timer = setTimeout(() => {
      // Filter to steps whose targets exist
      const existing = allSteps.filter((s) => document.querySelector(s.target));
      if (existing.length === 0) return;
      setValidSteps(existing);
      setCurrentStep(0);
      setActive(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, [loading, session, role, storageKey, allSteps]);

  // Position the tooltip relative to the target element
  const positionTooltip = useCallback(() => {
    if (!active || validSteps.length === 0) return;
    const step = validSteps[currentStep];
    if (!step) return;

    const el = document.querySelector(step.target) as HTMLElement | null;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const padding = 6;

    // Highlight box
    setHighlightStyle({
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Tooltip position — clamp to viewport on small screens
    const tooltipWidth = Math.min(280, window.innerWidth - 24);
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 160;
    const gap = 12;
    let top = 0;
    let left = 0;

    switch (step.position) {
      case "right":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + gap;
        break;
      case "left":
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - gap;
        break;
      case "bottom":
        top = rect.bottom + gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case "top":
        top = rect.top - tooltipHeight - gap;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
    }

    // Clamp to viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (left + tooltipWidth > vw - 12) left = vw - tooltipWidth - 12;
    if (left < 12) left = 12;
    if (top + tooltipHeight > vh - 12) top = vh - tooltipHeight - 12;
    if (top < 12) top = 12;

    setTooltipStyle({ top, left, width: tooltipWidth });
  }, [active, validSteps, currentStep]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener("resize", positionTooltip);
    window.addEventListener("scroll", positionTooltip, true);
    return () => {
      window.removeEventListener("resize", positionTooltip);
      window.removeEventListener("scroll", positionTooltip, true);
    };
  }, [positionTooltip]);

  const completeTour = useCallback(() => {
    localStorage.setItem(storageKey, "true");
    setActive(false);
    setCurrentStep(0);
  }, [storageKey]);

  const goNext = useCallback(() => {
    if (currentStep >= validSteps.length - 1) {
      completeTour();
      return;
    }
    setTransitioning(true);
    setTimeout(() => {
      setCurrentStep((s) => s + 1);
      setTransitioning(false);
    }, 200);
  }, [currentStep, validSteps.length, completeTour]);

  // Public method to restart the tour (called via custom event)
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(storageKey);
      const existing = allSteps.filter((s) => document.querySelector(s.target));
      if (existing.length === 0) return;
      setValidSteps(existing);
      setCurrentStep(0);
      setTransitioning(false);
      setActive(true);
    };
    window.addEventListener("restart-onboarding", handler);
    return () => window.removeEventListener("restart-onboarding", handler);
  }, [storageKey, allSteps]);

  if (!active || validSteps.length === 0) return null;

  const step = validSteps[currentStep];

  return (
    <div className="fixed inset-0 z-[100]" style={{ pointerEvents: "auto" }}>
      {/* SVG backdrop with spotlight cutout */}
      <svg
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <mask id="onboarding-mask">
            <rect width="100%" height="100%" fill="white" />
            <rect
              x={highlightStyle.left as number}
              y={highlightStyle.top as number}
              width={highlightStyle.width as number}
              height={highlightStyle.height as number}
              rx="8"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#onboarding-mask)"
        />
      </svg>

      {/* Highlight ring around target */}
      <div
        className="absolute rounded-lg ring-2 ring-accent/70 transition-all duration-300 ease-out"
        style={{
          ...highlightStyle,
          pointerEvents: "none",
          boxShadow: "0 0 0 4px rgba(99,102,241,0.15), 0 0 20px rgba(99,102,241,0.2)",
        }}
      />

      {/* Click blocker (behind tooltip) */}
      <div
        className="absolute inset-0"
        onClick={completeTour}
        style={{ pointerEvents: "auto" }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`absolute z-[101] rounded-xl border border-border bg-surface shadow-2xl p-4 transition-all duration-300 ease-out ${
          transitioning ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
        style={{ ...tooltipStyle, pointerEvents: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step counter */}
        <div className="text-[10px] font-medium text-text-muted mb-1">
          {currentStep + 1} de {validSteps.length}
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-text mb-1">{step.title}</h3>

        {/* Description */}
        <p className="text-xs text-text-muted leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={completeTour}
            className="text-[11px] text-text-muted hover:text-text transition px-2 py-1 rounded-lg hover:bg-surface-hover"
          >
            Omitir
          </button>
          <button
            onClick={goNext}
            className="text-[11px] font-semibold text-white bg-accent hover:bg-accent/90 transition px-4 py-1.5 rounded-lg"
          >
            {currentStep >= validSteps.length - 1 ? "Finalizar" : "Siguiente"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingTour;
